/**
 * VolumeChart.jsx
 * What each monitor counted, day by day.
 *
 * One line per monitor on one axis, which is safe here in a way it was not on
 * the dashboard: every series is the same measure — a count of things found —
 * so they are genuinely comparable. Colours come from the validated
 * categorical palette; past its documented all-pairs limit the remaining
 * monitors fold into a muted "other" rather than inventing hues.
 *
 * Every series is also named in the legend and reachable in the figures table,
 * so identity never rests on colour alone.
 *
 * Dependencies: components/Icons.jsx
 */

import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Chart, Table } from '../Icons.jsx';

const PAD = { left: 44, right: 14, top: 12, bottom: 24 };
const HEIGHT = 230;

/**
 * Three slots, because three is what the palette validates for all-pairs
 * comparison — which is what a multi-line chart needs. Beyond that, series are
 * drawn muted and named rather than given invented colours.
 */
export const SLOTS = 3;

const shortDate = (day) =>
  new Date(`${day}T00:00:00Z`).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  });

function niceMax(value) {
  if (value <= 0) return 1;
  const magnitude = 10 ** Math.floor(Math.log10(value));
  const steps = [1, 1.5, 2, 2.5, 3, 4, 5, 7.5, 10];
  return magnitude * (steps.find((s) => value <= magnitude * s) ?? 10);
}

export default function VolumeChart({ monitors = [], readings = [], days }) {
  const wrapRef = useRef(null);
  const [width, setWidth] = useState(560);
  const [active, setActive] = useState(null);
  const [showTable, setShowTable] = useState(false);

  useLayoutEffect(() => {
    const el = wrapRef.current;
    if (!el) return undefined;
    const observer = new ResizeObserver(([entry]) =>
      setWidth(Math.max(320, Math.round(entry.contentRect.width))),
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => setActive(null), [readings]);

  // Every day any monitor was read, in order — the shared x-axis.
  const days_ = [...new Set(readings.map((r) => r.day))].sort();

  // Only monitors that actually have readings; an empty line is noise.
  const withData = monitors.filter((m) => readings.some((r) => r.monitor_id === m.id));

  const series = withData.map((m, i) => ({
    id: m.id,
    name: m.name,
    slot: i < SLOTS ? i + 1 : null,
    points: days_.map((d) => readings.find((r) => r.monitor_id === m.id && r.day === d)?.volume ?? null),
  }));

  const max = niceMax(Math.max(1, ...readings.map((r) => r.volume ?? 0)));
  const plotWidth = Math.max(1, width - PAD.left - PAD.right);
  const plotHeight = HEIGHT - PAD.top - PAD.bottom;

  const x = (i) => PAD.left + (days_.length < 2 ? plotWidth / 2 : (i / (days_.length - 1)) * plotWidth);
  const y = (v) => PAD.top + plotHeight - (v / max) * plotHeight;

  const path = (points) => {
    let d = '';
    let pen = false;
    points.forEach((v, i) => {
      // A gap is a day nobody looked, not a zero. The line breaks rather than
      // dropping to the floor and inventing a collapse.
      if (v === null) {
        pen = false;
        return;
      }
      d += `${pen ? 'L' : 'M'}${x(i).toFixed(1)} ${y(v).toFixed(1)} `;
      pen = true;
    });
    return d.trim();
  };

  function locate(event) {
    const box = event.currentTarget.getBoundingClientRect();
    if (days_.length < 2) return 0;
    const i = Math.round(((event.clientX - box.left - PAD.left) / plotWidth) * (days_.length - 1));
    return Math.min(days_.length - 1, Math.max(0, i));
  }

  const ticks = days_.length < 2 ? [0] : [0, Math.floor((days_.length - 1) / 2), days_.length - 1];

  return (
    <section className="card">
      <div className="card__head">
        <span className="tile">
          <Chart size={21} />
        </span>
        <div className="card__head-text">
          <h2>What your monitors counted</h2>
          <p>Per day, over the last {days} days.</p>
        </div>
        <div className="card__head-end">
          <ul className="viz-legend">
            {series.map((s) => (
              <li key={s.id}>
                <span
                  className={`viz-legend__key ${s.slot ? `mon-key--${s.slot}` : 'mon-key--other'}`}
                />
                {s.name}
              </li>
            ))}
          </ul>
        </div>
      </div>

      {days_.length < 2 ? (
        <div className="empty empty--inline">
          <div className="empty__art">
            <Chart size={26} />
          </div>
          <h3>Not enough readings yet</h3>
          <p>
            Monitors are checked once a night, and a chart needs two days to draw. Press Check now
            to take a first reading.
          </p>
        </div>
      ) : (
        <>
          <div className="viz" ref={wrapRef}>
            <svg
              width={width}
              height={HEIGHT}
              role="img"
              aria-label={`Daily counts for ${series.length} monitors`}
              onPointerMove={(e) => setActive(locate(e))}
              onPointerLeave={() => setActive(null)}
            >
              {[0, 0.5, 1].map((f) => (
                <line
                  key={f}
                  className="viz-grid"
                  x1={PAD.left}
                  x2={width - PAD.right}
                  y1={PAD.top + plotHeight - f * plotHeight}
                  y2={PAD.top + plotHeight - f * plotHeight}
                />
              ))}

              {[0, 1].map((f) => (
                <text
                  key={f}
                  className="viz-tick"
                  x={PAD.left - 8}
                  y={PAD.top + plotHeight - f * plotHeight + 4}
                  textAnchor="end"
                >
                  {f === 0 ? '0' : max}
                </text>
              ))}

              {series.map((s) => (
                <path
                  key={s.id}
                  className={`viz-line ${s.slot ? `mon-line--${s.slot}` : 'mon-line--other'}`}
                  d={path(s.points)}
                />
              ))}

              {active !== null && (
                <line
                  className="viz-crosshair"
                  x1={x(active)}
                  x2={x(active)}
                  y1={PAD.top - 4}
                  y2={PAD.top + plotHeight}
                />
              )}

              {active !== null &&
                series.map((s) =>
                  s.points[active] === null ? null : (
                    <circle
                      key={s.id}
                      className={`viz-dot ${s.slot ? `mon-dot--${s.slot}` : 'mon-dot--other'}`}
                      cx={x(active)}
                      cy={y(s.points[active])}
                      r="4"
                    />
                  ),
                )}

              {ticks.map((i) => (
                <text
                  key={i}
                  className="viz-tick"
                  x={x(i)}
                  y={HEIGHT - 6}
                  textAnchor={i === 0 ? 'start' : i === days_.length - 1 ? 'end' : 'middle'}
                >
                  {shortDate(days_[i])}
                </text>
              ))}
            </svg>

            {active !== null && (
              <div
                className="viz-tip"
                style={{ left: `${Math.min(Math.max(x(active), 80), width - 80)}px` }}
              >
                <div className="viz-tip__day">{shortDate(days_[active])}</div>
                {series.map((s) => (
                  <div className="viz-tip__row" key={s.id}>
                    <span
                      className={`viz-legend__key ${s.slot ? `mon-key--${s.slot}` : 'mon-key--other'}`}
                    />
                    <strong>{s.points[active] ?? '—'}</strong>
                    <span>{s.name}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="dash-chart__foot">
            <span className="row__sub">
              A gap is a day nothing was checked, not a day nothing happened.
            </span>
            <button className="btn btn--sm" onClick={() => setShowTable((v) => !v)}>
              <Table size={15} /> {showTable ? 'Hide figures' : 'Show figures'}
            </button>
          </div>

          {showTable && (
            <div className="viz-table-wrap">
              <table className="viz-table">
                <thead>
                  <tr>
                    <th scope="col">Day</th>
                    {series.map((s) => (
                      <th scope="col" key={s.id}>
                        {s.name}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {[...days_].reverse().map((d) => (
                    <tr key={d}>
                      <th scope="row">{shortDate(d)}</th>
                      {series.map((s) => (
                        <td key={s.id}>{s.points[days_.indexOf(d)] ?? '—'}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </section>
  );
}
